import { useState, useEffect } from "react";
import { celebrationsApi } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Cake, Trophy, CalendarHeart } from "lucide-react";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const formatMMDD = (mmdd) => {
  if (!mmdd || mmdd.length !== 5) return "";
  const [m, d] = mmdd.split("-");
  return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${parseInt(d, 10)}`;
};

const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const CelebrationsWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    celebrationsApi
      .get()
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) return null;

  const hasToday =
    (data.today_birthdays?.length || 0) +
      (data.today_anniversaries?.length || 0) >
    0;
  const hasUpcoming =
    (data.upcoming_birthdays?.length || 0) +
      (data.upcoming_anniversaries?.length || 0) >
    0;

  if (!hasToday && !hasUpcoming) return null;

  const currentMonthLabel = MONTH_NAMES[new Date().getMonth()];

  return (
    <Card
      className="border-pink-200 dark:border-pink-900/50 bg-gradient-to-br from-pink-50 via-amber-50 to-white dark:from-pink-950/40 dark:via-amber-950/20 dark:to-transparent"
      data-testid="celebrations-widget"
    >
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-foreground">
          <CalendarHeart className="text-pink-500" size={20} />
          Celebrations
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {hasToday && (
          <div className="space-y-2" data-testid="celebrations-today">
            <div className="text-xs font-semibold uppercase tracking-wide text-pink-600 dark:text-pink-400">
              Today
            </div>
            <ul className="space-y-1.5">
              {data.today_birthdays?.map((b) => (
                <li
                  key={`b-${b.user_id}`}
                  className="flex items-center gap-2 text-sm text-foreground"
                >
                  <Cake className="text-pink-500 shrink-0" size={16} />
                  <span className="font-medium">{b.name}</span>
                  <span className="text-muted-foreground">— Happy Birthday!</span>
                </li>
              ))}
              {data.today_anniversaries?.map((a) => (
                <li
                  key={`a-${a.user_id}`}
                  className="flex items-center gap-2 text-sm text-foreground"
                >
                  <Trophy className="text-amber-500 shrink-0" size={16} />
                  <span className="font-medium">{a.name}</span>
                  <span className="text-muted-foreground">
                    — {ordinal(a.years)} work anniversary
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {hasUpcoming && (
          <div className="space-y-2" data-testid="celebrations-upcoming">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Upcoming in {currentMonthLabel}
            </div>
            <ul className="space-y-1.5">
              {data.upcoming_birthdays?.map((b) => (
                <li
                  key={`ub-${b.user_id}`}
                  className="flex items-center gap-2 text-sm text-foreground"
                >
                  <Cake className="text-pink-400 shrink-0" size={15} />
                  <span className="font-medium">{b.name}</span>
                  <span className="text-muted-foreground">
                    — {formatMMDD(b.birthday)}
                  </span>
                </li>
              ))}
              {data.upcoming_anniversaries?.map((a) => (
                <li
                  key={`ua-${a.user_id}`}
                  className="flex items-center gap-2 text-sm text-foreground"
                >
                  <Trophy className="text-amber-400 shrink-0" size={15} />
                  <span className="font-medium">{a.name}</span>
                  <span className="text-muted-foreground">
                    — {formatMMDD(a.mm_dd)} ({ordinal(a.years + 1)} year)
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CelebrationsWidget;
